/**
 * CCDB BFF API — Event Details
 * Backend for Frontend API for CCDB collection app.
 *
 * NOTE: This class is generated following OpenAPI Generator patterns.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {EventType} from '../models/EventType';
import type {EventStatus} from '../models/EventStatus';
import type {EventDetail} from '../models/EventDetail';
import type {CcdbError} from '../models/CcdbError';

// ============================================
// Fetch Event Types
// ============================================

export interface FetchEventTypes$Request {}

export interface FetchEventTypes$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const fetchEventTypesRequest = createAction<PayloadData<void, FetchEventTypes$Request>>('CCDB_BFF__FETCH_EVENT_TYPES__REQUEST');
export const fetchEventTypesSuccess = createAction<PayloadData<EventType[], FetchEventTypes$Request>>('CCDB_BFF__FETCH_EVENT_TYPES__SUCCESS');
export const fetchEventTypesFailure = createAction<PayloadData<CcdbError, FetchEventTypes$Request, true>>('CCDB_BFF__FETCH_EVENT_TYPES__FAILURE');

export const fetchEventTypesActions = [
    fetchEventTypesRequest,
    fetchEventTypesSuccess,
    fetchEventTypesFailure,
] as const;

/**
 * Fetch all event types for dropdown
 */
export const fetchEventTypes = (meta?: FetchEventTypes$Request, config?: FetchEventTypes$Config) => {
    return {
        CALL_API: {
            types: fetchEventTypesActions,
            method: ApiMethods.GET,
            endpoint: '/api/event-types',
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            },
        },
        ...(meta ?? {}),
    };
};

// ============================================
// Fetch Statuses
// ============================================

export interface FetchStatuses$Request {}

export interface FetchStatuses$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const fetchStatusesRequest = createAction<PayloadData<void, FetchStatuses$Request>>('CCDB_BFF__FETCH_STATUSES__REQUEST');
export const fetchStatusesSuccess = createAction<PayloadData<EventStatus[], FetchStatuses$Request>>('CCDB_BFF__FETCH_STATUSES__SUCCESS');
export const fetchStatusesFailure = createAction<PayloadData<CcdbError, FetchStatuses$Request, true>>('CCDB_BFF__FETCH_STATUSES__FAILURE');

export const fetchStatusesActions = [
    fetchStatusesRequest,
    fetchStatusesSuccess,
    fetchStatusesFailure,
] as const;

/**
 * Fetch all statuses for dropdown
 */
export const fetchStatuses = (meta?: FetchStatuses$Request, config?: FetchStatuses$Config) => {
    return {
        CALL_API: {
            types: fetchStatusesActions,
            method: ApiMethods.GET,
            endpoint: '/api/statuses',
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            },
        },
        ...(meta ?? {}),
    };
};

// ============================================
// Fetch Event Detail by ID
// ============================================

export interface FetchEventDetail$Request {
    eventId: string;
}

export interface FetchEventDetail$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const fetchEventDetailRequest = createAction<PayloadData<void, FetchEventDetail$Request>>('CCDB_BFF__FETCH_EVENT_DETAIL__REQUEST');
export const fetchEventDetailSuccess = createAction<PayloadData<EventDetail, FetchEventDetail$Request>>('CCDB_BFF__FETCH_EVENT_DETAIL__SUCCESS');
export const fetchEventDetailFailure = createAction<PayloadData<CcdbError, FetchEventDetail$Request, true>>('CCDB_BFF__FETCH_EVENT_DETAIL__FAILURE');

export const fetchEventDetailActions = [
    fetchEventDetailRequest,
    fetchEventDetailSuccess,
    fetchEventDetailFailure,
] as const;

/**
 * Fetch event detail by event ID
 * GET /api/events?eventId={eventId}
 */
export const fetchEventDetail = (meta: FetchEventDetail$Request, config?: FetchEventDetail$Config) => {
    return {
        CALL_API: {
            types: fetchEventDetailActions,
            method: ApiMethods.GET,
            endpoint: `/api/events?eventId=${encodeURIComponent(meta.eventId)}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            },
        },
        ...meta,
    };
};
